import { Dispatch, SetStateAction, useEffect } from 'react';
import {
  Label, ComboBox, Input, RadioText, RecordInterface,
} from '@components/common/Forms';
import { useTranslation } from 'next-i18next';
import { useJobLanguages } from '@components/templates/job-add/edit/hooks/useJobLanguages';
import { JobLanguagePill } from '@components/templates/job-add/edit/form/partials/JobLanguagePill';
import { GlobeAltIcon } from '@heroicons/react/24/outline';

/** Props */
interface JobSpokenLanguagesProps {
  record: RecordInterface,
  initialSelectedValues?: ApiSchema<'SpokenLanguageWrite2'>[],
  apiSpokenLanguages?: ApiSchema<'OptionItem'>[],
  apiSpokenLanguageLevels?: ApiSchema<'OptionItem'>[],
  spokenLanguagesSetter?: Dispatch<SetStateAction<ApiSchema<'SpokenLanguageWrite2'>[] | undefined>>,
  labelDescriptionClass?: string,
}

/**
 * @description The spoken languages combobox and the selected languages pills.
 * - `record` - record function from useBjForm
 * - `initialSelectedValues` - the initial selected languages from API.
 * - `apiSpokenLanguages` - the list of spoken languages from API.
 * - `apiSpokenLanguageLevels` - the list of spoken language levels from API.
 * - `spokenLanguagesSetter` - the function to set the selected languages for passing them to the parent component.
 * - `labelDescriptionClass` - additional classes for the label description
 */
export const JobSpokenLanguages = (props: JobSpokenLanguagesProps) => {
  // Destructure props
  const {
    record,
    initialSelectedValues,
    apiSpokenLanguages,
    apiSpokenLanguageLevels,
    spokenLanguagesSetter,
    labelDescriptionClass = '',
  } = props;

  // Job languages hook
  const {
    staticList,
    selectedLanguages,
    filterStaticList,
    addLanguage,
    deleteLanguage,
    changeLanguageLevel,
  } = useJobLanguages({
    initialSelectedValues,
    apiSpokenLanguages,
  });

  // Translation
  const { t } = useTranslation('common');

  // Set the selected languages to the parent component
  useEffect(() => {
    if (spokenLanguagesSetter) spokenLanguagesSetter(selectedLanguages);
  }, [selectedLanguages, spokenLanguagesSetter]);

  // Render component
  // **********************************************
  return (
    <div className="mt-4 grid gap-4">
      {/* Spoken languages combobox */}
      <Label
        description={t('global.filter.spoken_lang')}
        descriptionClass={labelDescriptionClass}
        className="!mt-0"
        as="div"
      >
        <div className="relative">
          <GlobeAltIcon
            className="absolute left-3 top-6 z-1 size-5 -translate-y-1/2 text-ink"
          />
          <ComboBox
            placeholder={t('onboarding.step5.example.placeholder')}
            className="text-ellipsis pl-10 pr-18"
            options={{
              staticList,
              forceRefreshStaticList: true,
              clearOnSelect: true,
              valueFromKey: 'name',
              filteringFunction: filterStaticList,
              freeSolo: false,
              showResetButton: false,
            }}
            onSelectedItemChange={(item) => {
              if (item) addLanguage(item as ApiSchema<'OptionItem'>);
            }}
            renderList={(
              item,
              index,
              listKey,
              getItemProps,
              listStyling,
            ) => (
              <li
                key={listKey}
                className={`${listStyling} cursor-pointer`}
                {...getItemProps({ item, index })}
              >
                {item['name' as keyof typeof item]}
              </li>
            )}
          />
        </div>
      </Label>

      {/* Selected languages pills */}
      <div className="flex flex-wrap gap-2">
        {selectedLanguages.map((languageItem) => (
          <JobLanguagePill
            key={languageItem.shortName}
            languageItem={languageItem}
            languageLevels={apiSpokenLanguageLevels}
            languagesList={apiSpokenLanguages}
            changeLanguageLevel={changeLanguageLevel}
            deleteLanguage={deleteLanguage}
          />
        ))}
      </div>

      {/* Make the selected languages required input */}
      <label
        htmlFor="allLanguagesRequired"
        className="ml-3 flex cursor-pointer items-start justify-self-start text-sm"
      >
        <Input
          className="text-primary-light checked:bg-primary-light"
          type="checkbox"
          {...record('allLanguagesRequired', {
            convertValue: 'toBoolean',
            defaultValue: 'true',
          })}
        />
        <RadioText>
          {t('global.filter.other_lang.all')}
        </RadioText>
      </label>
    </div>
  );
};
